"use client";

import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { ReadMoreBlock } from "@/components/ReadMoreBlock";
import { IconHub, IconSettingsRemote, IconTerminal } from "@/components/icons";
import {
  hrefForServiceCard,
  type ServiceCardDef,
  type ServiceCardIconId,
} from "@/config/service-cards";
import { cn } from "@/lib/utils";
import { CapabilityHubProse } from "@/components/services/CapabilityHubProse";
import type { CapabilityHubContent } from "@/components/services/capability-hub-types";

const serviceIcons: Record<ServiceCardIconId, typeof IconTerminal> = {
  terminal: IconTerminal,
  remote: IconSettingsRemote,
  hub: IconHub,
};

const accentBarClass = [
  "before:bg-[var(--primary)]",
  "before:bg-[color-mix(in_srgb,var(--primary)_48%,var(--design-ambient-tint)_52%)]",
  "before:bg-[color-mix(in_srgb,var(--design-ambient-tint)_45%,var(--primary)_55%)]",
] as const;

export function CapabilityArticleExpandable({
  indexLabel,
  card,
  articleIndex = 0,
}: {
  indexLabel: string;
  card: ServiceCardDef;
  articleIndex?: number;
}) {
  const t = useTranslations("serviceCards");
  const tArticle = useTranslations("capabilityArticle");
  const reduceMotion = useReducedMotion();
  const href = hrefForServiceCard(card);
  const msgKey = card.capability;
  const title = t(`${msgKey}.title`);
  const hub = t.raw(`${msgKey}.hub`) as CapabilityHubContent;
  const [firstParagraph, ...restParagraphs] = hub.paragraphs;
  const Icon = serviceIcons[card.iconId];
  const accent = accentBarClass[articleIndex % accentBarClass.length]!;
  const enterDelay = articleIndex * 0.07;

  return (
    <motion.div
      className="relative min-w-0"
      initial={reduceMotion ? undefined : { opacity: 0, y: 28 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2, margin: "0px 0px -8% 0px" }}
      transition={{
        duration: 0.48,
        delay: enterDelay,
        ease: [0.25, 1, 0.5, 1],
      }}
    >
      <div
        className={cn(
          "group relative overflow-hidden rounded-[var(--radius-md)] border border-[color-mix(in_srgb,var(--outline-variant)_32%,transparent)] bg-[var(--surface)]",
          "pl-5 pr-4 py-6 sm:pl-6 sm:pr-5 sm:py-8 shadow-[0_28px_70px_-36px_color-mix(in_srgb,var(--primary)_22%,transparent)]",
          "motion-safe:transition-[box-shadow,border-color,transform] motion-safe:duration-300 motion-safe:ease-[var(--ease-out-quint)]",
          "before:pointer-events-none before:absolute before:inset-y-5 before:left-0 before:w-[3px] before:rounded-full before:content-['']",
          accent,
          "hover:border-[color-mix(in_srgb,var(--outline-variant)_48%,transparent)] hover:shadow-[0_36px_80px_-32px_color-mix(in_srgb,var(--primary)_26%,transparent)]",
        )}
      >
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3 mb-5 sm:mb-6">
          <span
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[color-mix(in_srgb,var(--primary-container)_95%,transparent)] text-[var(--primary)] ring-1 ring-[color-mix(in_srgb,var(--outline-variant)_20%,transparent)] motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-[var(--ease-out-quint)] motion-safe:group-hover:scale-[1.03]"
            aria-hidden
          >
            <span className="[&_svg]:h-[22px] [&_svg]:w-[22px]">
              <Icon />
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span
                className="tabular-nums text-sm text-[var(--on-surface-variant)]/50 font-medium tracking-tight"
                style={{ fontFamily: "var(--font-body)" }}
                aria-hidden
              >
                {indexLabel}
              </span>
              <h3
                className="font-display text-2xl sm:text-[clamp(1.55rem,2.6vw,1.95rem)] text-[var(--on-surface)] tracking-tight"
                style={{ fontFamily: "var(--font-display)" }}
              >
                <Link
                  href={href}
                  className="hover:text-[var(--primary)] motion-safe:transition-colors motion-safe:duration-200 decoration-transparent hover:decoration-[var(--primary)] underline-offset-[6px]"
                >
                  {title}
                </Link>
              </h3>
            </div>
          </div>
        </div>

        <ReadMoreBlock
          readFullHref={href}
          readFullLabel={tArticle("readFull")}
          collapsedContent={
            <p
              className="text-[var(--on-surface-variant)] leading-relaxed text-[1rem] sm:text-[1.0625rem] max-w-[40rem]"
              style={{ fontFamily: "var(--font-body)" }}
            >
              {firstParagraph}
            </p>
          }
          expandedContent={
            <CapabilityHubProse
              hub={{
                paragraphs: restParagraphs,
                pillars: hub.pillars,
                closing: hub.closing,
              }}
              className="pt-1"
            />
          }
        />
      </div>
    </motion.div>
  );
}
